import type { IconType } from 'react-icons'
import { HiBookOpen, HiCalendarDays, HiShieldCheck, HiSquares2X2, HiUserGroup } from 'react-icons/hi2'
import { getVisibleEntries } from '../../config/searchRegistry'
import type { AccessFlags } from '../../contexts/UserPermissionsContext'
import { toSafeExternalUrl } from '../../common/safeExternalUrl'
import type { HandbookArticle } from '../../services/handbookService'
import { getResourceIcon } from './resourceIcons'
import { canShowHandbookLink } from '../../common/handbookAccess'
import { DUO_TRAX_LINK, hasInterviewNavigation, hasManagerNavigation, isDuoTraxLink } from '../../common/navigationRules'
import { leaveViewPath } from '../leave/leaveView'
import { isRetiredFeatureLink } from '../../common/retiredFeatures'

export type SearchCategory = 'All' | 'My Profile' | 'Quick Actions' | 'Pages' | 'Team Apps' | 'Company Services' | 'Knowledge' | 'Admin' | 'People' | 'Bills' | 'Tasks' | 'Documents' | 'Leave' | 'IT Tickets' | 'Calendar' | 'Memos'

export type ResourceKind = 'Actions' | 'Apps' | 'Knowledge' | 'People' | 'Records'
export interface Resource {
  id: string
  label: string
  path: string
  detail: string
  keywords?: string
  kind: ResourceKind
  icon?: IconType
  imageSrc?: string
  searchCategory?: SearchCategory
  teamApp?: boolean
  /** The Admin → Company Services category, for grouping links the same way as the header. */
  categoryName?: string
}
export interface AppItem {
  app_name: string
  app_url: string
  icon_path?: string
  visibility?: string
  company?: string
  location?: string
  category_name?: string
}
export interface Policy {
  policy_id: number
  name: string
  office: string
  status: string
  needs_reread?: boolean
}

const ROUTE_FLAGS: Record<string, keyof AccessFlags> = {
  '/bills': 'bills', '/cafe': 'cafe', '/directory': 'directory',
  '/system-development': 'systemDev', '/duo-services': 'duoServices',
  '/dqs': 'dqsAdmin',
  '/sharepoint-access': 'dqsAdmin', '/wellbeing-allowance': 'wellbeingAllowance',
  '/admin': 'adminPanel',
}

export function canOpenResource(path: string, flags: AccessFlags, documentsVisible: boolean) {
  if (isRetiredFeatureLink(path)) return false
  if (!path || !toSafeExternalUrl(path)) return false
  if (!canShowHandbookLink(path, flags.systemDev === true)) return false
  const pathname = new URL(path, window.location.origin).pathname
  if (pathname === '/documents' && !documentsVisible) return false
  return Object.entries(ROUTE_FLAGS).every(([prefix, flag]) =>
    !(pathname === prefix || pathname.startsWith(`${prefix}/`)) || flags[flag] === true
  )
}

export function buildResources(
  permissions: Record<string, any>, email: string, flags: AccessFlags,
  documentsVisible: boolean, policiesVisible: boolean,
  teamApps: AppItem[], services: AppItem[],
  { shutdownRosterAccess = false }: { shutdownRosterAccess?: boolean } = {},
): Resource[] {
  const entries = getVisibleEntries({ userPermissions: permissions, userEmail: email,
    documentsVisible, billsAccess: flags.bills, wellbeingAllowanceAccess: flags.wellbeingAllowance,
    duoServicesAccess: flags.duoServices })
  const resources: Resource[] = entries.filter(entry => entry.group !== 'Admin' && entry.path !== '/').map(entry => ({
    id: entry.path, label: entry.label, path: entry.path, keywords: entry.keywords, icon: entry.icon,
    searchCategory: entry.group,
    detail: entry.group === 'Quick Actions' ? 'Start a request' : 'Duo Hub',
    kind: entry.group === 'Quick Actions' ? 'Actions' :
      ['/my-team', '/directory', '/org-chart'].includes(entry.path) ? 'People' :
        ['/documents', '/case-studies', '/minutes'].includes(entry.path) ? 'Knowledge' : 'Apps',
  }))
  if (flags.systemDev) resources.push({ id: '/handbook', label: 'Handbook', path: '/handbook',
    detail: 'Procedures and how-to guides', keywords: 'sop process procedure guide instructions knowledge',
    kind: 'Knowledge', icon: HiBookOpen })
  if (policiesVisible) resources.push({ id: '/policies', label: 'Company policies', path: '/policies',
    detail: 'Guidance for your workplace', keywords: 'policy policies sick leave rules benefits',
    kind: 'Knowledge', icon: HiShieldCheck })
  if (flags.directory && !resources.some(r => r.path === '/directory')) resources.push({
    id: '/directory', label: 'Company directory', path: '/directory', detail: 'Find a colleague',
    keywords: 'people staff contact phone', kind: 'People', icon: HiUserGroup,
  })
  for (const [apps, teamApp] of [[teamApps, true], [services, false]] as const) {
    for (const app of apps) {
      // Mirror the existing navigation's audience restrictions, including missing audience fields.
      if (app.visibility === 'inactive') continue
      if (app.visibility === 'admin_only' && permissions.Team__c !== 'System Development') continue
      if (app.company !== 'All' && (!permissions.Company__c || app.company !== permissions.Company__c)) continue
      if (app.location !== 'All' && (!permissions.Division_Location__c || app.location !== permissions.Division_Location__c)) continue
      resources.push({ id: app.app_url, path: app.app_url, label: app.app_name,
        detail: teamApp ? `For ${permissions.Team__c}` : app.category_name || 'Company app',
        keywords: app.category_name, searchCategory: teamApp ? 'Team Apps' : 'Company Services', kind: 'Apps', icon: HiSquares2X2, teamApp,
        categoryName: app.category_name || undefined,
        ...getResourceIcon(app.app_name, app.app_url, app.icon_path) })
    }
  }
  // The header grants managers a Trax shortcut even when their team has no
  // database assignment. Include it in My Apps, without duplicating configured links.
  if (hasManagerNavigation(permissions, email) && !resources.some(item => isDuoTraxLink(item.path, item.label))) {
    resources.push({ ...DUO_TRAX_LINK, id: DUO_TRAX_LINK.path, kind: 'Apps', teamApp: true,
      detail: 'Team performance and reporting', keywords: 'manager team reporting',
      ...getResourceIcon(DUO_TRAX_LINK.label, DUO_TRAX_LINK.path) })
  }
  // Surface Interviews alongside the permitted interviewer's team apps.
  if (hasInterviewNavigation(permissions, email)) {
    resources.push({ id: '/interviews', label: 'Interviews', path: '/interviews', kind: 'Apps', teamApp: true,
      detail: 'Candidates, schedules and feedback', keywords: 'hiring recruitment candidates interview' })
  }
  if (shutdownRosterAccess) resources.push({
    id: leaveViewPath('shutdown'), path: leaveViewPath('shutdown'), label: 'Skeleton Crew',
    detail: 'View your team’s working days during the company shutdown',
    keywords: 'shutdown skeleton crew staffing roster holiday calendar',
    kind: 'Apps', icon: HiCalendarDays,
  })
  const renamed: Record<string, [string, string]> = {
    '/leave/new': ['Request leave', 'Time off, sick leave and holidays'],
    '/it-tickets': ['Get IT help', 'Report an issue or check your tickets'],
    '/bills/new': ['Submit an expense', 'Bills, receipts and reimbursements'],
    '/meeting-room': ['Book a room', 'Find and reserve a meeting room'],
    '/cafe': ['Order a coffee', 'Coffee and food from Duo Cafe'],
    '/my-profile': ['My profile', 'Contact, bank and personal details'],
    '/kpi': ['My assessments', 'Performance reviews and goals'],
    '/leave': ['My leave', 'Requests, balances and approvals'],
    '/security-training': ['Security training', 'Required learning and modules'],
    '/memo': ['Company memos', 'Read company communications'],
    '/interviews': ['Interviews', 'Candidates, schedules and feedback'],
  }
  const unique = new Map<string, Resource>()
  for (const item of resources) {
    if (item.path === leaveViewPath('shutdown') && !shutdownRosterAccess) continue
    if (!canOpenResource(item.path, flags, documentsVisible)) continue
    const existing = unique.get(item.path)
    if (existing) {
      existing.keywords = `${existing.keywords || ''} ${item.label} ${item.keywords || ''}`
      existing.teamApp ||= item.teamApp
      existing.categoryName ||= item.categoryName
      if (item.imageSrc) existing.imageSrc = item.imageSrc
      if (item.icon && item.icon !== HiSquares2X2) existing.icon = item.icon
    } else {
      const copy = { ...item }
      copy.keywords = `${item.label} ${item.keywords || ''}`
      if (renamed[item.path]) [copy.label, copy.detail] = renamed[item.path]
      unique.set(item.path, copy)
    }
  }
  return [...unique.values()].map(resource => resource.imageSrc ? resource : { ...resource, ...getResourceIcon(resource.label, resource.path) })
}

export function articleResources(articles: HandbookArticle[]): Resource[] {
  return articles.filter(a => a.is_published && !a.deleted_at).map(a => ({
    id: `handbook-${a.id}`, label: a.title, path: `/handbook/${encodeURIComponent(a.slug)}`,
    detail: a.description || a.category_name || 'Handbook article',
    keywords: `${a.category_name || ''} ${a.markdown_content || ''}`,
    kind: 'Knowledge', icon: HiBookOpen,
  }))
}

export function rankResources(items: Resource[], query: string): Resource[] {
  const terms = query.toLocaleLowerCase().trim().split(/\s+/).filter(Boolean)
  if (!terms.length) return items
  return items.map(item => {
    const label = item.label.toLocaleLowerCase()
    const text = `${label} ${item.detail} ${item.keywords || ''}`.toLocaleLowerCase()
    const score = terms.every(term => text.includes(term))
      ? (label === query.trim().toLocaleLowerCase() ? 100 : 0) +
        terms.reduce((total, term) => total + (label.includes(term) ? 10 : 1), 0)
      : 0
    return { item, score }
  }).filter(result => result.score > 0).sort((a, b) => b.score - a.score).map(result => result.item)
}

export function readSavedPaths(key: string): string[] | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const value: unknown = JSON.parse(raw)
    return Array.isArray(value) ? [...new Set(value.filter((p): p is string => typeof p === 'string'))] : null
  } catch { return null }
}

export function defaultShortcuts(items: Resource[]) {
  const candidates = [
    ...items.filter(item => item.teamApp).slice(0, 2).map(item => item.path),
    '/leave/new', '/it-tickets', '/handbook', '/bills/new', '/meeting-room', '/cafe', '/my-profile',
  ]
  return [...new Set(candidates)].filter(path => items.some(item => item.path === path)).slice(0, 6)
}
